import crypto from "crypto";

// Shared key derivation. The translate script and the build-time lookup must
// agree exactly, so both normalise and hash through these two functions.

/** Collapse whitespace and trim, so re-wrapped JSX text keeps the same key. */
export function normalizeTranslationText(value: string): string {
  return String(value).replace(/\s+/g, " ").trim();
}

/**
 * Decodes the handful of HTML entities the converted pages carry. Without this,
 * "Chest &amp; Lung" and "Chest & Lung" would hash to different keys and get
 * translated twice.
 */
export function decodeEntities(value: string): string {
  return String(value)
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;|&rsquo;/g, "'")
    .replace(/&ldquo;|&rdquo;/g, '"')
    .replace(/&ndash;/g, "–")
    .replace(/&mdash;/g, "—");
}

export function getTranslationKey(value: string): string {
  return crypto
    .createHash("sha256")
    .update(normalizeTranslationText(decodeEntities(value)))
    .digest("hex")
    .slice(0, 16);
}

/**
 * Strings that are not prose and must never be sent for translation or
 * replaced: phone numbers, emails, URLs, pure numbers/punctuation.
 */
export function isTranslatable(value: string): boolean {
  const text = normalizeTranslationText(value);
  if (text.length < 2) return false;
  if (!/[A-Za-z]/.test(text)) return false; // numbers, punctuation, counters
  if (/^(https?:\/\/|mailto:|tel:|\/|#)/.test(text)) return false;
  if (/^[\w.+-]+@[\w.-]+\.\w+$/.test(text)) return false;
  if (/^[A-Za-z]+$/.test(text) && text.length <= 2) return false;
  return true;
}
